import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChatInput } from './ChatInput';
import { Sidebar } from '../Sidebar';
import { ChatContent } from './ChatContent';
import { ImageViewer } from '../imageviewer/ImageViewer';
import { useChatMessages } from '@/hooks/useChatMessages';
import { cn } from '@/utils';
import { useImageState, useImageActions } from '@/contexts/ImageContext';

const getChatIdFromSearchParams = (searchParams: URLSearchParams): number | null => {
  const rawChatId = searchParams.get('chatId');

  if (!rawChatId) {
    return null;
  }

  if (!/^\d+$/.test(rawChatId)) {
    return null;
  }

  const parsedChatId = Number.parseInt(rawChatId, 10);
  return parsedChatId > 0 ? parsedChatId : null;
};

export function Chat() {
  const [searchParams, setSearchParams] = useSearchParams();
  const routeChatId = useMemo(() => getChatIdFromSearchParams(searchParams), [searchParams]);
  const [chatId, setChatId] = useState<number | null>(routeChatId);
  
  // Get image state and actions from context
  const { isHalfWidth, isSidebarOpen, isLocalInpaintingOpen, localUploadImageUrl } = useImageState();
  const { setSidebarOpen, deselectImage, openLocalInpainting, setLocalUploadImage } = useImageActions();

  const {
    messages,
    isTyping,
    isLoading,
    error,
    errorDetails,
    currentChatId,
    sideBarReload,
    isRetrying,
    sendMessage,
    clearMessages,
    retryLastMessage,
    clearError,
    loadOlderAttachments,
    isLoadingOlderAttachments
  } = useChatMessages(chatId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingOlderRef = useRef<boolean>(false);
  const initialAutoScrollDoneRef = useRef<boolean>(false);
  const previousLocalUploadImageUrlRef = useRef<string>('');

  const syncChatIdToUrl = useCallback((nextChatId: number | null, replace = false) => {
    setSearchParams((previousSearchParams) => {
      const nextSearchParams = new URLSearchParams(previousSearchParams);

      if (nextChatId === null) {
        nextSearchParams.delete('chatId');
      } else {
        nextSearchParams.set('chatId', String(nextChatId));
      }

      return nextSearchParams;
    }, { replace });
  }, [setSearchParams]);

  useEffect(() => {
    setChatId((previousChatId) => {
      if (previousChatId === routeChatId) {
        return previousChatId;
      }

      return routeChatId;
    });
  }, [routeChatId]);

  // Auto-scroll only if user is near the bottom to avoid dragging view while loading older content
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 80; // px
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < threshold) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  // Ensure initial scroll is at the bottom after first load per chat
  useEffect(() => {
    if (!isLoading && messages.length > 0 && !initialAutoScrollDoneRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      initialAutoScrollDoneRef.current = true;
    }
  }, [isLoading, messages.length]);

  // Reset initial auto-scroll flag when chat changes
  useEffect(() => {
    initialAutoScrollDoneRef.current = false;
  }, [currentChatId]);

  const revokeLocalBlobUrl = useCallback((url: string) => {
    if (!url.startsWith('blob:')) {
      return;
    }

    try {
      URL.revokeObjectURL(url);
    } catch (error) {
      console.warn('Failed to revoke object URL:', url, error);
    }
  }, []);

  // Revoke previous local blob URL whenever the value changes (including when cleared).
  useEffect(() => {
    const previousUrl = previousLocalUploadImageUrlRef.current;
    if (previousUrl && previousUrl !== localUploadImageUrl) {
      revokeLocalBlobUrl(previousUrl);
    }
    previousLocalUploadImageUrlRef.current = localUploadImageUrl;
  }, [localUploadImageUrl, revokeLocalBlobUrl]);

  // Revoke any remaining blob URL on unmount.
  useEffect(() => {
    return () => {
      const previousUrl = previousLocalUploadImageUrlRef.current;
      if (previousUrl) {
        revokeLocalBlobUrl(previousUrl);
      }
    };
  }, [revokeLocalBlobUrl]);

  // Sync local chatId with currentChatId from hook when new chat is created
  // Only sync when we were creating a new chat (chatId was null) and got a new ID back
  useEffect(() => {
    if (chatId === null && currentChatId !== null) {
      console.log('Syncing new chat ID:', currentChatId);
      setChatId(currentChatId);
      syncChatIdToUrl(currentChatId, true);
    }
  }, [currentChatId, chatId, syncChatIdToUrl]);

  const handleNewChat = () => {
    console.log('Creating new chat...');
    setChatId(null);
    syncChatIdToUrl(null);
    clearMessages();
  };

  const handleChatSelect = (selectedChatId: number) => {
    console.log('Selected chat ID:', selectedChatId);
    setChatId(selectedChatId);
    syncChatIdToUrl(selectedChatId);
  };

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const handleCloseImageViewer = () => {
    deselectImage(); // resets isHalfWidth, isLocalInpaintingOpen, localUploadImageUrl
  };

  /* Opens the ImageViewer right panel in local-upload mode  */
  const handleInpainting = () => {
    openLocalInpainting();
  };

  // Lazy load older attachments when user scrolls near the top
  const handleScrollLoadOlder = useCallback(async () => {
    const el = scrollContainerRef.current;
    if (!el || isLoading || isTyping) return;
    // Trigger when the user is near top (<= 40px)
    if (el.scrollTop <= 40 && !isLoadingOlderRef.current) {
      isLoadingOlderRef.current = true;
      try {
        const prevScrollHeight = el.scrollHeight;
        const prevScrollTop = el.scrollTop;
        await loadOlderAttachments(5);
        // Preserve visual position after DOM updates by compensating height growth
        requestAnimationFrame(() => {
          const newScrollHeight = el.scrollHeight;
          el.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
        });
      } finally {
        // Small delay to avoid thrashing when staying at top
        setTimeout(() => { isLoadingOlderRef.current = false; }, 150);
      }
    }
  }, [isLoading, isTyping, loadOlderAttachments]);



  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Sidebar */}
      <Sidebar
        isOpen={isSidebarOpen}
        onToggle={toggleSidebar}
        currentChatId={currentChatId}
        onNewChat={handleNewChat}
        onChatSelect={handleChatSelect}
        sideBarReload={sideBarReload}
        onInpainting={handleInpainting}
      />

      {/* Main Chat Area */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'ml-0'}`}>
        {/* Messages Container */}
        <div className="flex justify-start h-screen overflow-hidden">
          <div ref={scrollContainerRef} onScroll={handleScrollLoadOlder} className={cn(
            isHalfWidth === true ? 'w-1/2' : 'w-full',

            'flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 overflow-y-scroll scrollbar-thin scrollbar-thumb-rounded'
          )}>
            <div className="max-w-4xl mx-auto px-4 py-6 min-h-full flex flex-col justify-start ">
              {isLoadingOlderAttachments && (
                <div className="flex justify-center py-2">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
                </div>
              )}

              <ChatContent
                messages={messages}
                chatState={{
                  isLoading,
                  isTyping,
                  error,
                  errorDetails,
                  chatId
                }}
                onRetryMessage={retryLastMessage}
                onClearError={clearError}
                isRetrying={isRetrying}
                ref={messagesEndRef}
              />
            </div>
          </div>

          {isHalfWidth === true && (messages.length > 1 || isLocalInpaintingOpen) && (
            <ImageViewer
              onClose={handleCloseImageViewer}
              onSendMessage={sendMessage}
              onNewChat={handleNewChat}
              isTyping={isTyping}
              messages={messages}
              isLocalInpaintingMode={isLocalInpaintingOpen}
              localUploadImageUrl={localUploadImageUrl || undefined}
              onLocalImageUpload={(url) => {
                setLocalUploadImage(url);
              }}
            />
          )}
        </div>
        
        {/* Input - only show when NOT in half-width mode */}
        {!isHalfWidth && (
          <ChatInput
            onSendMessage={sendMessage}
            disabled={isTyping}
            onNewChat={handleNewChat}
            currentChatId={currentChatId}
          />
        )}
      </div>
    </div>
  );
}